import { useMemo } from 'react';
import type { RefObject } from 'react';

import type { JSONContent } from '@tiptap/core';
import { EditorContent } from '@tiptap/react';
import {
  sanitizeRichTextContent,
  type NumberedListItem,
} from '@course-authoring/shared-types';
import { createStylizedListStyles } from '@course-authoring/ui-blocks';

import type { StylizedNumberedListEditorStyles } from './stylizedNumberedListEditorStyles';
import { createRichTextEditorExtensions } from './richTextEditorExtensions';
import {
  preventRichTextToolbarMouseDown,
  RichTextToolbar,
} from './richTextToolbar';
import { useRichTextFieldEditor } from './useRichTextFieldEditor';
import { getInteractionRichTextFieldPresetConfig } from './interactionEditorUtils';

type StylizedListStyles = ReturnType<typeof createStylizedListStyles>;

const fullPresetConfig = getInteractionRichTextFieldPresetConfig('full');

interface StylizedNumberedListItemEditorProps {
  item: NumberedListItem;
  index: number;
  isOnlyItem: boolean;
  isFocused: boolean;
  isBulleted: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onContentChange: (content: JSONContent) => void;
  onDelete: () => void;
  autoFocus: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  listStyles: StylizedListStyles;
  editorStyles: StylizedNumberedListEditorStyles;
  toolbarOffset: string;
  translationMode?: boolean;
}

export function StylizedNumberedListItemEditor({
  item,
  index,
  isOnlyItem,
  isFocused,
  isBulleted,
  onFocus,
  onBlur,
  onContentChange,
  onDelete,
  autoFocus,
  containerRef,
  listStyles,
  editorStyles,
  toolbarOffset,
  translationMode = false,
}: StylizedNumberedListItemEditorProps) {
  const sanitizedContent = useMemo(
    () => sanitizeRichTextContent(item.content),
    [item.content]
  );
  const extensions = useMemo(
    () => createRichTextEditorExtensions(
      `Enter item ${index + 1} text...`,
      fullPresetConfig.extensionOptions
    ),
    [index]
  );

  const editor = useRichTextFieldEditor({
    extensions,
    content: sanitizedContent,
    onContentChange,
    onFocus,
    onBlur,
    containerRef,
    editorClassName: 'tiptap snl-item-editor',
    editorInlineStyle: 'outline: none; min-height: 24px; width: 100%;',
    autoFocus,
    shouldRerenderOnTransaction: true,
  });

  if (!editor) {
    return <div style={editorStyles.itemContainer} />;
  }

  return (
    <div style={editorStyles.itemContainer}>
      <div
        style={{
          ...editorStyles.itemToolbar,
          marginLeft: toolbarOffset,
          ...(isFocused ? {} : { opacity: 0.5, pointerEvents: 'none' as const }),
        }}
        onMouseDown={preventRichTextToolbarMouseDown}
      >
        <RichTextToolbar
          editor={editor}
          items={fullPresetConfig.toolbarItems}
          toolbarStyle={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}
          buttonStyle={editorStyles.toolbarButton}
          activeButtonStyle={editorStyles.toolbarButtonActive}
        />
        <div style={editorStyles.toolbarSpacer} />
        {!translationMode ? <button
          onClick={onDelete}
          disabled={isOnlyItem}
          style={{
            ...editorStyles.deleteButton,
            ...(isOnlyItem ? editorStyles.deleteButtonDisabled : {}),
          }}
          title={isOnlyItem ? 'Cannot delete the only item' : 'Delete item'}
          type="button"
        >
          {'\u2715'}
        </button> : null}
      </div>

      <div style={listStyles.item}>
        {isBulleted ? (
          <span aria-hidden="true" style={listStyles.bulletMarker} />
        ) : (
          <div aria-hidden="true" style={listStyles.numberMarker}>
            {index + 1}
          </div>
        )}
        <EditorContent
          editor={editor}
          style={{ ...listStyles.itemContent, ...editorStyles.editor }}
        />
      </div>
    </div>
  );
}
